/**
 * Backend timestamp parsing for the web app.
 *
 * A timestamp with no timezone designator is read in the zone the caller
 * declares (`NaiveTimestampZone`) rather than handed to the engine, which
 * would always assume local wall-clock time:
 *
 * - `'utc'` (default): the value is a UTC instant, so it is built with
 *   `Date.UTC`.
 * - `'local'`: the endpoint already converted the value with the
 *   `timezoneOffset` sent on the request (feed, group posts, comments,
 *   notifications), so the string is local wall-clock time and reading it as
 *   UTC would shift the age by the viewer's offset. Mirrors the shared
 *   `dateHelpers.parseToDate` contract.
 *
 * Nothing here adds or subtracts an offset; the returned `Date` is an absolute
 * instant that the UI renders in the viewer's timezone.
 */

export type NaiveTimestampZone = 'utc' | 'local';

/** Values the API already formatted for display — there is no instant to read. */
const RELATIVE_LABEL_PATTERN =
  /^(just now|yesterday|\d+\s*(s|sec|secs|m|min|mins|h|hr|hrs|d|day|days|w|wk|wks|week|weeks|mo|mos|month|months|y|yr|yrs|year|years)\b.*)$/i;

/** `YYYY-MM-DD[T ]HH:mm[:ss[.fraction]]`, no timezone designator. */
const NAIVE_ISO_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/;

/** `YYYY-MM-DD` calendar date, no time component. */
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `DD-MM-YYYY[ HH:mm[:ss]]`. */
const DMY_PATTERN =
  /^(\d{2})-(\d{2})-(\d{4})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/** .NET short date, e.g. `8/7/2026 5:22:01 AM`. */
const DOTNET_PATTERN =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i;

const EXPLICIT_ZONE_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Epoch values below this are seconds, not milliseconds. */
const MS_THRESHOLD = 1e12;

const build = (
  zone: NaiveTimestampZone,
  year: number,
  month: number,
  day: number,
  hours = 0,
  minutes = 0,
  seconds = 0,
  ms = 0,
): Date | null => {
  const date =
    zone === 'utc'
      ? new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, ms))
      : new Date(year, month - 1, day, hours, minutes, seconds, ms);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** .NET returns up to 7 fractional digits; JavaScript only keeps milliseconds. */
const toMilliseconds = (fraction?: string): number =>
  fraction ? Number(fraction.slice(0, 3).padEnd(3, '0')) : 0;

/**
 * Reads a backend timestamp as an absolute instant, or `null` when the value
 * is missing, already a relative label, or unparseable.
 */
export function parseApiTimestamp(
  value: string | number | Date | null | undefined,
  naiveZone: NaiveTimestampZone = 'utc',
): Date | null {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const date = new Date(value < MS_THRESHOLD ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const raw = value.trim();
  if (!raw) return null;
  if (RELATIVE_LABEL_PATTERN.test(raw)) return null;

  // Explicit zone: the string already pins the instant.
  if (EXPLICIT_ZONE_PATTERN.test(raw)) {
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = raw.match(NAIVE_ISO_PATTERN);
  if (iso) {
    return build(
      naiveZone,
      Number(iso[1]),
      Number(iso[2]),
      Number(iso[3]),
      Number(iso[4]),
      Number(iso[5]),
      iso[6] ? Number(iso[6]) : 0,
      toMilliseconds(iso[7]),
    );
  }

  const dateOnly = raw.match(DATE_ONLY_PATTERN);
  if (dateOnly) {
    return build(naiveZone, Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3]));
  }

  const dmy = raw.match(DMY_PATTERN);
  if (dmy) {
    return build(
      naiveZone,
      Number(dmy[3]),
      Number(dmy[2]),
      Number(dmy[1]),
      dmy[4] ? Number(dmy[4]) : 0,
      dmy[5] ? Number(dmy[5]) : 0,
      dmy[6] ? Number(dmy[6]) : 0,
    );
  }

  const dotnet = raw.match(DOTNET_PATTERN);
  if (dotnet) {
    let hours = dotnet[4] ? Number(dotnet[4]) : 0;
    const meridiem = (dotnet[7] || '').toUpperCase();
    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    return build(
      naiveZone,
      Number(dotnet[3]),
      Number(dotnet[1]),
      Number(dotnet[2]),
      hours,
      dotnet[5] ? Number(dotnet[5]) : 0,
      dotnet[6] ? Number(dotnet[6]) : 0,
    );
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Milliseconds between the backend instant and now. Never negative: a clock
 * a little ahead of the server would otherwise read as a future post.
 */
export function elapsedSince(date: Date, now: number = Date.now()): number {
  return Math.max(0, now - date.getTime());
}
